import React, { FC, useState } from 'react'
import DropTrailerIcon from 'transfix-icons/build/DropTrailerIcon'
import { Switch } from 'transfix-ui/components/Switch'
import { useGlobalToast } from 'transfix-ui/components/Toast/GlobalToast'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import Box from 'transfix-ui/core/Box'
import Grid from 'transfix-ui/core/Grid'
import { createStyles, makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import {
  ShipperCityCarrierLane as TLane,
  useUpdateShipperCityCarrierLane,
  RateType,
} from 'apps/carrier/apiTypes/shipperCityCarrierLanes'
import { capitalize } from 'utils/capitalize'
import { toggleRate } from './FuelSurcharge'

interface IShipperCityCarrierLaneProps {
  lane: TLane
}

const useStyles = makeStyles(() =>
  createStyles({
    laneRow: {
      transition: 'background 300ms ease-in',
      '&:hover': {
        background: brandColors.white,
      },
    },
  })
)

/**
 * Shipper City Carrier Lane Row
 */
const ShipperCityCarrierLane: FC<IShipperCityCarrierLaneProps> = ({ lane }) => {
  const { openToast } = useGlobalToast()
  const { userPermissions } = useUserDetails()
  const hasEditPermission = userPermissions?.canEditCarrierCompanyInformation
  const [rateType, setRateType] = useState<RateType>(lane.rateType)
  const { loading, mutate: updateRateType } = useUpdateShipperCityCarrierLane({
    laneId: lane.id,
  })
  const classes = useStyles()

  const toggleRateType = async () => {
    setRateType(current => toggleRate(current))

    const resp = await updateRateType({
      shipperCityCarrierLane: {
        rateType: toggleRate(rateType),
      },
    })

    openToast({
      toastMessage: `Updated to ${capitalize(resp.rateType.split('_').join(' '))}`,
      toastType: 'default',
    })
  }

  return (
    <Box padding={1} className={classes.laneRow}>
      <Grid
        container
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        wrap='nowrap'>
        <Grid item xs={1}>
          <Box textAlign='center'>{lane.shipperCityLaneId}</Box>
        </Grid>
        <Grid item xs={2} zeroMinWidth>
          {lane.shipperApplicationName}
        </Grid>
        <Grid item xs={1}>
          <Box display='flex' justifyContent='center'>
            {lane.isDropTrailer && (
              <Tooltip
                position='bottom'
                trigger={
                  <span>
                    <DropTrailerIcon size='large' />
                  </span>
                }>
                Drop Trailer
              </Tooltip>
            )}
          </Box>
        </Grid>
        <Grid item xs={2}>
          {lane.pickup.city}, {lane.pickup.stateCode}
        </Grid>
        <Grid item xs={2}>
          {lane.delivery.city}, {lane.delivery.stateCode}
        </Grid>
        <Grid item xs={2}>
          <Box display='flex' justifyContent='center'>
            <Tooltip
              position='bottom'
              trigger={
                <Switch
                  disabled={loading || !hasEditPermission}
                  checked={rateType === 'fuel_surcharge'}
                  onClick={toggleRateType}
                />
              }>
              {capitalize(rateType.split('_').join(' '))}
            </Tooltip>
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

export default ShipperCityCarrierLane
